import { useQueryClient } from "react-query";
import { CheckCircle, XCircle } from "phosphor-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "../../../../../components/Button";
import { useAuth } from "../../../../../contexts/AuthContext";
import { DialogModal } from "../../../../../components/DialogModal";
import { InternationalBankDataForm } from "../../../../../components/Forms/InternationalBankDataForm";
import { useExpertInternationalBankDataMutation } from "../../../../../services/hooks/experts/mutations/useExpertInternationalBankDataMutation";
import { useBankDataQuery } from "../../../../../services/hooks/experts/queries/useExpertBankDataQuery";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { toast } from "react-toastify";
import { type InternationalBankDataPersistence } from "../../../../../types/persistence/international-person-persistence";
import register3 from "../../../../../assets/images/register-3.png";

interface Props {
  onGoPreviousStep: () => void;
}

export const InternationalBankData = ({ onGoPreviousStep }: Props) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useExpertInternationalBankDataMutation();
  const { user } = useAuth();
  const { data: bank } = useBankDataQuery(user?.id!);

  const [isSuccessFeedbackModalOpen, setIsSuccessFeedbackModalOpen] =
    useState(false);

  const handleSaveInternacionalBankData = (
    info: InternationalBankDataPersistence
  ) => {
    if (user?.id) {
      mutate(
        { id: user?.id, data: info },
        {
          onSuccess(result) {
            queryClient.setQueryData(["expert", user.id, "bank-data"], {
              swiftCode: result.swiftCode,
              bankAccountNumber: result.bankAccountNumber,
              internationalBankBranch: result.internationalBankBranch,
              amazonGiftCardEmail: result.amazonGiftCardEmail,
            });
            queryClient.invalidateQueries("expert");
            setIsSuccessFeedbackModalOpen(true);
          },
          onError(error: unknown) {
            toast(getErrorMessage(error), {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              closeButton: false,
              icon: (
                <XCircle
                  size={48}
                  className="mx-auto my-auto text-alert-error"
                />
              ),
              className: "bg-red-50",
            });
          },
        }
      );
    }
  };

  return (
    <section className="md:py-5">
      <div className="container mx-auto">
        <div className="flex justify-center">
          <div className="hidden md:block w-1/4">
            <img src={register3} alt="Text with image" />
          </div>
          <div className="w-full md:w-3/4 md:ps-8">
            <div className="mb-10">
              <h2 className="mb-6 text-2xl font-semibold">
                {formatMessage({ id: "REGISTER_BANK_DATA" })}
              </h2>

              <p className="mb-4 font-normal text-grey-800 max-w-[622px]">
                {formatMessage({
                  id: "FINISH_YOUR_INTERNATIONAL_BANK_ACCOUNT_REGISTRATION",
                })}
              </p>

              <p className="text-sm font-normal">
                {formatMessage({ id: "DESCRIPTION_PAYMENT_DATA" })}
              </p>
            </div>

            <InternationalBankDataForm
              isSubmitting={isLoading}
              defaultValues={{
                swiftCode: bank?.swiftCode ?? "",
                bankAccountNumber: bank?.bankAccountNumber ?? "",
                internationalBankBranch: bank?.internationalBankBranch ?? "",
                amazonGiftCardEmail: bank?.amazonGiftCardEmail ?? "",
              }}
              onSubmit={handleSaveInternacionalBankData}
              userId={user?.id}
              onGoPreviousStep={onGoPreviousStep}
            />
          </div>

          <DialogModal
            isOpen={isSuccessFeedbackModalOpen}
            onClose={() => setIsSuccessFeedbackModalOpen(false)}
          >
            <div className="pb-10">
              <header className="mb-14">
                <CheckCircle
                  size={78}
                  className="mx-auto mb-16 text-primary-light"
                />

                <strong className="block font-normal text-center">
                  {formatMessage({ id: "REGISTRATION_COMPLETED_SUCCESSFULLY" })}
                </strong>
              </header>

              <footer className="mx-auto w-60">
                <Button onClick={() => navigate("/expert")} fullWidth>
                  {formatMessage({ id: "CONTINUE" })}
                </Button>
              </footer>
            </div>
          </DialogModal>
        </div>
      </div>
    </section>
  );
};
